import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getD1ChartData,
  getD2ChartData,
  getD3ChartData,
  getD4ChartData,
  getD5ChartData,
  getD6ChartData,
  getD7ChartData,
  getD8ChartData,
  getD9ChartData,
  getD10ChartData,
  getD11ChartData,
  getD12ChartData,
  getD16ChartData,
  getD20ChartData,
  getD24ChartData,
  getD27ChartData,
  getD30ChartData,
  getD40ChartData,
  getD45ChartData,
  getD60ChartData
  // , getMahadasha
} from '../api';
import logger from '../utils/logger';
import KundliChart from './utils/KundliChart';
import Dropdown from './utils/Dropdown'; // reuse existing dropdown component

const CHART_TYPES = [
  { id: 'D1', label: 'D1 (Rashi)', description: 'Basic Birth Chart', fn: getD1ChartData },
  { id: 'D2', label: 'D2 (Hora)', description: 'Wealth & Finance', fn: getD2ChartData },
  { id: 'D3', label: 'D3 (Drekkana)', description: 'Siblings & Courage', fn: getD3ChartData },
  { id: 'D4', label: 'D4 (Chaturthamsha)', description: 'Property & Happiness', fn: getD4ChartData },
  { id: 'D5', label: 'D5 (Panchamsha)', description: 'Children & Creativity', fn: getD5ChartData },
  { id: 'D6', label: 'D6 (Shashthamsha)', description: 'Health & Enemies', fn: getD6ChartData },
  { id: 'D7', label: 'D7 (Saptamsha)', description: 'Partnership & Marriage', fn: getD7ChartData },
  { id: 'D8', label: 'D8 (Ashtamsha)', description: 'Longevity & Crisis', fn: getD8ChartData },
  { id: 'D9', label: 'D9 (Navamsha)', description: 'Marriage & Relationships', fn: getD9ChartData },
  { id: 'D10', label: 'D10 (Dasamsha)', description: 'Career & Profession', fn: getD10ChartData },
  { id: 'D11', label: 'D11 (Rudramsha)', description: 'Income & Earnings', fn: getD11ChartData },
  { id: 'D12', label: 'D12 (Dwadashamsha)', description: 'Parents & Ancestral Wealth', fn: getD12ChartData },
  { id: 'D16', label: 'D16 (Shodashamsha)', description: 'Vehicles & Conveyances', fn: getD16ChartData },
  { id: 'D20', label: 'D20 (Vimshamsha)', description: 'Spiritual Practices', fn: getD20ChartData },
  { id: 'D24', label: 'D24 (Chaturvimshamsha)', description: 'Education & Intellect', fn: getD24ChartData },
  { id: 'D27', label: 'D27 (Saptavimshamsha)', description: 'Health & Stamina', fn: getD27ChartData },
  { id: 'D30', label: 'D30 (Trimshamsha)', description: 'Misfortune & Calamities', fn: getD30ChartData },
  { id: 'D40', label: 'D40 (Chatvarimshamsa)', description: 'General Results', fn: getD40ChartData },
  { id: 'D45', label: 'D45 (Akshavedamsha)', description: 'Combined Effects', fn: getD45ChartData },
  { id: 'D60', label: 'D60 (Shashtiamsha)', description: 'Ultimate Divisional Chart', fn: getD60ChartData }
  // , { id: 'MAHA', label: 'Mahadasha', description: 'Planetary periods (Mahadasha/Antardasha)', fn: getMahadasha }
];

function Kundli() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedChartType, setSelectedChartType] = useState('D1');
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  // row-based expanded cards for sibling visibility
  const [expandedRows, setExpandedRows] = useState(new Set());
  const cardRefs = useRef([]);
  // dropdown is always visible now, no need for separate toggle state

  useEffect(() => {
    if (!user) {
      logger.log('🔐 [Kundli] User not authenticated, redirecting to auth');
      navigate('/');
    }
    const timer = setTimeout(() => setShowWelcome(false), 1400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const buildChartData = useCallback((opts = {}) => {
    // If minimal flag is set, return only the fields backend expects for Mahadasha
    if (opts.minimal) {
      const payloadMin = {
        user_id: user?.userId || user?.id,
        profile_id: null
      };
      if (opts.start_date) payloadMin.start_date = opts.start_date;
      if (opts.end_date) payloadMin.end_date = opts.end_date;
      return payloadMin;
    }
    // Properly format datetime: extract date and time separately
    let dateTime = '2004-08-20T00:00:00';
    if (user?.birth_date) {
      const date = user.birth_date.split('T')[0]; // Extract just the date part
      let time = '00:00:00';
      if (user?.birth_time) {
        // Extract only HH:MM:SS from birth_time (handles both "HH:MM:SS" and full ISO formats)
        time = user.birth_time.includes('T') 
          ? user.birth_time.split('T')[1].split('.')[0] // From ISO datetime
          : user.birth_time.split('.')[0]; // From time string
      }
      dateTime = `${date}T${time}`;
    }

    // Determine numeric timezone to send to backend (default to 5.5 if unknown)
    let timezoneDecimal = 5.5;
    if (typeof opts.timezone === 'number') timezoneDecimal = opts.timezone;
    else if (typeof user?.timezone === 'number') timezoneDecimal = user.timezone;
    else if (typeof user?.timezone === 'string') {
      const tz = user.timezone.toLowerCase();
      if (tz.includes('kolkata') || tz === 'ist') timezoneDecimal = 5.5;
    }

    const payload = {
      user_id: user?.userId || user?.id,
      profile_id: null,
      name: user?.full_name || 'User',
      datetime: dateTime,
      latitude: user?.latitude || 0,
      longitude: user?.longitude || 0,
      timezone: timezoneDecimal,
      place: user?.birth_place || user?.place || 'Unknown',
      religion: user?.religion || 'Hindu'
    };

    // Include optional start/end for Mahadasha requests
    if (opts.start_date) payload.start_date = opts.start_date;
    if (opts.end_date) payload.end_date = opts.end_date;

    return payload;
  }, [user]);

  const fetchChartData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setChartData(null);

    try {
      if (!user?.userId && !user?.id) {
        setError('User not available');
        setIsLoading(false);
        return;
      }

      const chart = CHART_TYPES.find(c => c.id === selectedChartType);
      if (!chart) throw new Error('Invalid chart type');

      // For Mahadasha, include a date range (default: today -> +10 years)
      let requestData;
      if (selectedChartType === 'MAHA') {
        const today = new Date();
        const start = today.toISOString().split('T')[0];
        const endDate = new Date(today);
        endDate.setFullYear(endDate.getFullYear() + 10);
        const end = endDate.toISOString().split('T')[0];
        // Send full payload for Mahadasha (datetime, lat/lng, place, religion, timezone)
        requestData = buildChartData({ start_date: start, end_date: end });
      } else {
        requestData = buildChartData();
      }
      logger.log(`[Kundli] Requesting ${selectedChartType}`, requestData);

      const response = await chart.fn(requestData);

      if (!response?.data?.success) {
        const msg = response?.data?.message || 'Failed to load chart';
        throw new Error(msg);
      }

      const data = response.data.data || {};

        // Normalize Mahadasha response: some backends store detailed JSON in `result`
        if (data.result && typeof data.result === 'string') {
          try {
            const parsed = JSON.parse(data.result);
            // Prefer explicit dasha_periods, otherwise try parsed fields
            data.dasha_periods = data.dasha_periods || parsed.dasha_periods || parsed.dashaPeriods || parsed.periods || [];
            data.mahadasha = data.mahadasha || parsed.mahadasha || data.dasha_periods;
            // Also copy over any readable text
            data.mahadasha_text = data.mahadasha_text || parsed.summary || parsed.result_text || parsed.analysis?.summary || null;
            // merge other useful fields
            data.analysis = data.analysis || parsed.analysis || null;
          } catch (e) {
            // ignore parse errors
          }
        }


      // Ensure graha_table is always an array
      let table = data.graha_table || [];
      if (typeof table === 'string') {
        try { table = JSON.parse(table); } catch (e) { table = []; }
      }
      if (table && typeof table === 'object' && !Array.isArray(table)) {
        table = Object.values(table);
      }
      if (!Array.isArray(table)) table = [];

      data.graha_table = table;

      setChartData(data);
      logger.log(`[Kundli] Loaded ${selectedChartType}`, { count: table.length });
    } catch (err) {
      logger.error('[Kundli] Error fetching chart', err);
      setError(err.message || 'Failed to load chart');
    } finally {
      setIsLoading(false);
    }
  }, [selectedChartType, buildChartData, user]);

  const handleChartTypeChange = (chartType) => {
    setSelectedChartType(chartType);
    setChartData(null);
    setError(null);
    // Auto-fetch data immediately when selection changes
    setTimeout(() => {
      // Schedule fetch after state update
    }, 0);
  };

  // Auto-fetch when selectedChartType changes (except on initial mount)
  useEffect(() => {
    if (selectedChartType) {
      fetchChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChartType]);

  const columns = useMemo(() => {
    if (!chartData || !Array.isArray(chartData.graha_table)) return [];
    const set = new Set();
    chartData.graha_table.forEach(row => {
      Object.keys(row || {}).forEach(k => set.add(k));
    });
    // ensure 'Graha' or 'graha' is first column if present
    const keys = Array.from(set);
    const prefer = ['Graha', 'graha', 'grahaSymbol', 'GrahaSymbol'];
    prefer.reverse().forEach(p => {
      const idx = keys.indexOf(p);
      if (idx > -1) keys.unshift(...keys.splice(idx, 1));
    });
    return keys;
  }, [chartData]);

  const humanize = (key) => key
    .replace(/_/g, ' ')
    .replace(/\(/g, ' (')
    .replace(/\)/g, ')')
    .replace(/\b(\w)/g, (m) => m.toUpperCase());

  // Map common planet names to colors (used for planet cards)
  const getPlanetColor = (name = '') => {
    const key = String(name).toLowerCase();
    const map = {
      sun: '#FF4500', surya: '#FF4500',
      moon: '#FFFFFF', chandra: '#FFFFFF',
      mars: '#BB0000', mangal: '#BB0000',
      mercury: '#7B0000', budha: '#7B0000',
      jupiter: '#A52583', brihaspati: '#A52583',
      venus: '#00AA00', shukra: '#00AA00',
      saturn: '#4B4B4B', shani: '#4B4B4B',
      rahu: '#000000', ketu: '#8B4513'
    };
    return map[key] || '#999999';
  };

  const handleCardClick = useCallback((clickedIndex) => {
    const clickedEl = cardRefs.current[clickedIndex];
    if (!clickedEl) return;

    const clickedTop = clickedEl.offsetTop;
    const rowIndexes = cardRefs.current
      .map((el, idx) => (el && Math.abs(el.offsetTop - clickedTop) <= 4 ? idx : -1))
      .filter(idx => idx !== -1);

    if (rowIndexes.length === 0) return;

    setExpandedRows((prevRows) => {
      const rowAlreadyExpanded = rowIndexes.every(idx => prevRows.has(idx));
      const nextRows = new Set(prevRows);
      rowIndexes.forEach((idx) => {
        if (rowAlreadyExpanded) nextRows.delete(idx);
        else nextRows.add(idx);
      });
      return nextRows;
    });
  }, []);

  return (
    <div style={{ padding: '65px 0 65px 0', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header Section */}
      <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
        <h1 className="heading">Divisional Charts (Kundli)</h1>
        <p className="refer-subtitle">Explore your cosmic blueprint through astrology's wisdom</p>
      </div>




      {/* Error Message */}
      {error && (
        <div style={{ background: 'rgba(220, 38, 38, 0.12)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: '10px', padding: '12px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <span style={{ color: '#ff6b6b', fontSize: '14px' }}>{error}</span>
        </div>
      )}

      {/* Chart Data Display */}
      {(chartData || isLoading) && (
        <div className="welcome-card">
          {/* Dropdown positioned at top of results */}
          <div style={{ marginBottom: '20px' }}>
            <Dropdown
              items={CHART_TYPES.map(c => ({ value: c.id, label: c.label }))}
              value={selectedChartType}
              onChange={(val) => handleChartTypeChange(val)}
              placeholder="Choose chart..."
              menuClass="form-dropdown-menu"
              itemClass="form-dropdown-item"
              buttonClass="form-dropdown-container"
              className="chart-type-dropdown"
            />
          </div>

          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '12px', color: '#ffd700', fontSize: '16px' }}>
              <span>⚡</span>
              <span>Loading {selectedChartType} Chart...</span>
            </div>
          )}

          {chartData && !isLoading && (
            <div className="chart-details">
          {/* Mahadasha Display (for MAHA option) */}
          {selectedChartType === 'MAHA' && (
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ color: '#ffd700', marginBottom: '12px', fontSize: '16px' }}>🕰️ Mahadasha Timeline</h3>
              {chartData.mahadasha && Array.isArray(chartData.mahadasha) ? (
                <div style={{ overflowX: 'auto', background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <th style={{ padding: '8px', color: '#ffd700' }}>Mahadasha</th>
                        <th style={{ padding: '8px' }}>Start</th>
                        <th style={{ padding: '8px' }}>End</th>
                        <th style={{ padding: '8px' }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartData.mahadasha.map((m, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                          <td style={{ padding: '8px', color: '#fff', fontWeight: 600 }}>{m.name || m.planet || m.dasha || `Period ${i+1}`}</td>
                          <td style={{ padding: '8px', color: '#fff' }}>{m.start || m.from || m.begin || '-'}</td>
                          <td style={{ padding: '8px', color: '#fff' }}>{m.end || m.to || m.finish || '-'}</td>
                          <td style={{ padding: '8px', color: '#ccc' }}>{m.note || m.notes || m.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : chartData.mahadasha_text ? (
                <div style={{ color: '#fff', background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '8px' }}>{chartData.mahadasha_text}</div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#aaa' }}>📊 No Mahadasha data available for this chart</div>
              )}
            </div>
          )}

          {/* Kundli Chart Visualization - North Indian Style */}
          {selectedChartType !== 'MAHA' && chartData.graha_table && Array.isArray(chartData.graha_table) && (
            <KundliChart chartData={chartData} title={CHART_TYPES.find(ct => ct.id === selectedChartType)?.label || selectedChartType} />
          )}

          {/* Planet Cards view - each graha rendered as a card */}
          {chartData.graha_table && Array.isArray(chartData.graha_table) && (
            <div>
              <h3 style={{ color: '#ffd700', marginBottom: '12px', fontSize: '16px' }}>🪐 Planet Cards</h3>

              <div className="planet-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '12px', alignItems: 'start', gridAutoRows: 'minmax(0, auto)' }}>
                {chartData.graha_table.map((graha, index) => {
                  const name = graha.graha || graha.Graha || graha.planet || graha.name || `Planet ${index+1}`;
                  const color = getPlanetColor(name);
                  const longitude = graha.longitude || graha.Longitude || graha.lon || '-';
                  const ruler = graha.ruler || graha.Ruler || graha.lord || '-';
                  const dignity = graha.dignity || graha.Dignity || '-';
                  const relationship = graha.relationship || graha.Relationship || '-';
                  const isExpanded = expandedRows.has(index);

                  return (
                    <div
                      key={`card-${index}`}
                      ref={(el) => { cardRefs.current[index] = el; }}
                      className={`planet-card ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => handleCardClick(index)}
                      style={{
                        background: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.04)',
                        border: '1px solid rgba(255,255,255,0.04)',
                        borderRadius: 10,
                        padding: 12,
                        cursor: 'pointer',
                        boxShadow: isExpanded ? '0 6px 18px rgba(0,0,0,0.25)' : 'none'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 14, height: 14, borderRadius: 7, background: color, boxShadow: '0 0 6px rgba(0,0,0,0.2)' }} />
                          <div style={{ fontWeight: 700 }}>{name}</div>
                        </div>
                        <div style={{ color: '#ccc', fontSize: 12 }}>{longitude}</div>
                      </div>

                      

                      {isExpanded && (
                        <div style={{ marginTop: 10, borderTop: '1px dashed rgba(255,255,255,0.06)', paddingTop: 10 }}>
                          {Object.entries(graha).map(([k, v]) => (
                            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                              <div style={{ color: '#ccc', fontSize: 12 }}>{humanize(k)}</div>
                              <div style={{ color: '#fff', fontSize: 13 }}>{String(v)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No Data Message */}
          {chartData && !isLoading && selectedChartType !== 'MAHA' && (!chartData.graha_table || chartData.graha_table.length === 0) && (
            <div style={{ textAlign: 'center', padding: '30px', color: '#aaa' }}>
              <p>📊 No planetary data available for this chart</p>
            </div>
          )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Kundli;
